import { useEffect, useRef } from 'react';
import Btn from './Btn';
import Icon from './Icon';
import ChargeCell from './ChargeCell';
import RenderingNpiCell from './RenderingNpiCell';
import { useFeeSchedule } from '../../../hooks/useFeeSchedule';
import commonCpt from '../../../data/commonCpt.json';

/**
 * New service lines must not carry a synthetic id — the server's UUIDField
 * rejects "sl_…" strings, and an absent id means "create" in the upsert path.
 * Field names match the wire schema (claims/openapi.yaml#ClaimServiceLine);
 * any other names are silently dropped by DRF and clobbered on autosave.
 * Layout follows the prototype at
 * docs/superpowers/specs/assets/claim-creation-flow-end-to-end/project/sections.jsx::LineItemsSection.
 * @param {string} claimDateOfService
 */
function emptyLine(claimDateOfService) {
  return {
    service_date_from: claimDateOfService || '',
    service_date_to: claimDateOfService || '',
    place_of_service: '11',
    cpt_hcpcs_code: '',
    modifier_1: '', modifier_2: '', modifier_3: '', modifier_4: '',
    diagnosis_pointer_1: null,
    diagnosis_pointer_2: null,
    diagnosis_pointer_3: null,
    diagnosis_pointer_4: null,
    charge_amount: '',
    units: '1',
    rendering_provider_npi: '',
  };
}

/** Wire integers (1..12) → "A,B,..." display text. */
function pointersToText(/** @type {any} */ line) {
  const ptrs = [line.diagnosis_pointer_1, line.diagnosis_pointer_2, line.diagnosis_pointer_3, line.diagnosis_pointer_4];
  return ptrs
    .filter((n) => typeof n === 'number' && n >= 1 && n <= 12)
    .map((n) => String.fromCharCode('A'.charCodeAt(0) + /** @type {number} */ (n) - 1))
    .join(',');
}

/** "A,B,..." or "1,2,..." text → wire integers as a patch. */
function textToPointersPatch(/** @type {string} */ text) {
  const tokens = String(text || '')
    .toUpperCase()
    .split(/[\s,]+/)
    .filter(Boolean);
  /** @type {Record<string, number|null>} */
  const patch = {
    diagnosis_pointer_1: null, diagnosis_pointer_2: null,
    diagnosis_pointer_3: null, diagnosis_pointer_4: null,
  };
  tokens.slice(0, 4).forEach((token, i) => {
    const numeric = Number.parseInt(token, 10);
    const code = Number.isInteger(numeric)
      ? numeric
      : token.charCodeAt(0) - 'A'.charCodeAt(0) + 1;
    if (code >= 1 && code <= 12) patch[`diagnosis_pointer_${i + 1}`] = code;
  });
  return patch;
}

/** Lookup procedure description from common CPT data; empty when unknown. */
function describeCpt(/** @type {string} */ code) {
  const c = (/** @type {any[]} */ (commonCpt)).find((/** @type {any} */ x) => x.code === code);
  return c?.description || '';
}

/**
 * @param {{
 *   lines: Array<any>,
 *   payerId: string|null,
 *   claimRenderingNpi: string,
 *   claimDateOfService: string,
 *   onChange: (nextLines: Array<any>) => void,
 *   mode?: 'guided'|'paper',
 *   disabled?: boolean,
 * }} props
 */
export default function ServiceLineGrid({
  lines, payerId, claimRenderingNpi, claimDateOfService,
  onChange, mode = 'guided', disabled = false,
}) {
  const fee = useFeeSchedule(payerId);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Auto-resolve charge when CPT changes on a line that has no manual override.
  useEffect(() => {
    if (!Array.isArray(lines) || lines.length === 0) return;
    let changed = false;
    const next = lines.map((line) => {
      if (!line.cpt_hcpcs_code) return line;
      const resolved = fee.resolveCharge(line.cpt_hcpcs_code);
      // Leave the field blank when the schedule has no entry — user fills manually.
      if (!line.charge_amount && resolved.amount != null) {
        changed = true;
        return { ...line, charge_amount: resolved.amount };
      }
      return line;
    });
    if (changed) onChangeRef.current(next);
  }, [fee, lines]);

  const updateLine = (/** @type {number} */ idx, /** @type {Record<string,any>} */ patch) => {
    const next = lines.map((line, i) => (
      i === idx ? { ...line, line_number: line.line_number || i + 1, ...patch } : line
    ));
    onChange(next);
  };

  /** @param {number} idx @param {string} text */
  const updatePointersFromText = (idx, text) => {
    updateLine(idx, textToPointersPatch(text));
  };

  /** @param {number} idx @param {string} v */
  const updateDate = (idx, v) => {
    // Single date column mirrors the prototype: typing the FROM date
    // also fills the TO date. CMS-1500 Box 24A allows distinct ranges
    // but billers rarely enter them per-line at this step.
    const line = lines[idx];
    const patch = /** @type {Record<string, any>} */ ({ service_date_from: v });
    if (!line?.service_date_to || line.service_date_to === line.service_date_from) {
      patch.service_date_to = v;
    }
    updateLine(idx, patch);
  };

  const addLine = () => onChange([
    ...lines,
    { ...emptyLine(claimDateOfService), line_number: lines.length + 1 },
  ]);
  const deleteLine = (/** @type {number} */ idx) => {
    if (lines.length <= 1) return;
    onChange(lines.filter((_, i) => i !== idx));
  };

  const total = lines.reduce((s, l) => s + (Number(l.charge_amount) || 0), 0);
  const overrides = lines.filter(
    (l) => l.rendering_provider_npi && l.rendering_provider_npi !== claimRenderingNpi,
  ).length;
  const claimLevelDate = claimDateOfService || lines[0]?.service_date_from || '';

  const dense = mode === 'paper';
  const datalistId = 'cpt-list';

  return (
    <div>
      <div style={{
        overflow: 'auto',
        border: '1px solid var(--ab-border-hair)',
        borderRadius: 'var(--ab-radius-sm)',
      }}>
        <datalist id={datalistId}>
          {(/** @type {any[]} */ (commonCpt)).map((/** @type {any} */ c) => (
            <option key={c.code} value={c.code}>{c.description}</option>
          ))}
        </datalist>
        <table style={{
          width: '100%', borderCollapse: 'collapse',
          fontFamily: 'var(--ab-font-sans)',
          fontSize: dense ? 11 : 13,
          minWidth: 1040,
        }}>
          <thead>
            <tr style={{ background: 'var(--ab-bg-header)' }}>
              {[
                { label: '#', align: 'left' },
                { label: 'Date', align: 'left' },
                { label: 'CPT', align: 'left' },
                { label: 'Description', align: 'left' },
                { label: 'POS', align: 'left' },
                { label: 'Dx Ptr', align: 'left' },
                { label: 'Mod', align: 'left' },
                { label: 'Units', align: 'right' },
                { label: 'Charge', align: 'right' },
                { label: 'Total', align: 'right' },
                { label: 'Rendering NPI · 24J', align: 'left' },
                { label: '', align: 'right' },
              ].map((h, i) => (
                <th key={i} style={th(dense, /** @type {'left'|'right'|'center'} */ (h.align))}>{h.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, idx) => {
              const desc = describeCpt(line.cpt_hcpcs_code);
              const dateOverride = !!line.service_date_from
                && !!claimLevelDate
                && line.service_date_from !== claimLevelDate;
              return (
                <tr key={line.id || line.line_number || idx} style={{
                  background: idx % 2 ? 'var(--ab-bg-alt)' : 'var(--ab-bg)',
                }}>
                  <td style={td(dense, { fontWeight: 700, color: 'var(--ab-fg-subtle)' })}>{idx + 1}</td>

                  <td style={td(dense)}>
                    <input
                      data-field={`service_lines[${line.line_number || idx + 1}].service_date_from`}
                      type="date"
                      value={line.service_date_from || ''}
                      disabled={disabled}
                      onChange={(/** @type {any} */ e) => updateDate(idx, e.target.value)}
                      style={cellInput()}
                    />
                    {dateOverride && (
                      <span
                        title={`Differs from claim-level (${claimLevelDate}). Click to revert.`}
                        onClick={() => !disabled && updateLine(idx, {
                          service_date_from: claimLevelDate,
                          service_date_to: claimLevelDate,
                        })}
                        style={overrideBadge(dense, disabled)}
                      >{dense ? 'OVR' : 'OVERRIDE'}</span>
                    )}
                  </td>

                  <td style={td(dense)}>
                    <input
                      data-field={`service_lines[${line.line_number || idx + 1}].cpt_hcpcs_code`}
                      value={line.cpt_hcpcs_code || ''}
                      list={datalistId}
                      placeholder="CPT"
                      disabled={disabled}
                      onChange={(/** @type {any} */ e) => updateLine(idx, { cpt_hcpcs_code: e.target.value.toUpperCase() })}
                      style={{ ...cellInput(), width: 84, fontWeight: 700 }}
                    />
                  </td>

                  <td style={td(dense)}>
                    <input
                      readOnly
                      value={desc}
                      placeholder="—"
                      tabIndex={-1}
                      style={{
                        ...cellInput(),
                        color: 'var(--ab-fg-subtle)',
                        background: 'transparent',
                        cursor: 'default',
                      }}
                    />
                  </td>

                  <td style={td(dense)}>
                    <input
                      data-field={`service_lines[${line.line_number || idx + 1}].place_of_service`}
                      value={line.place_of_service || ''}
                      placeholder="11"
                      maxLength={2}
                      disabled={disabled}
                      onChange={(/** @type {any} */ e) => updateLine(idx, { place_of_service: e.target.value })}
                      style={{ ...cellInput(), width: 50 }}
                    />
                  </td>

                  <td style={td(dense)}>
                    <input
                      aria-label={`Line ${idx + 1} DX PTR`}
                      value={pointersToText(line)}
                      placeholder="A"
                      disabled={disabled}
                      onChange={(/** @type {any} */ e) => updatePointersFromText(idx, e.target.value)}
                      style={{ ...cellInput(), width: 70 }}
                    />
                  </td>

                  <td style={td(dense)}>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {[1, 2, 3, 4].map((n) => (
                        <input
                          key={n}
                          value={line[`modifier_${n}`] || ''}
                          maxLength={2}
                          placeholder="--"
                          disabled={disabled}
                          onChange={(/** @type {any} */ e) => updateLine(idx, {
                            [`modifier_${n}`]: e.target.value.toUpperCase(),
                          })}
                          style={{ ...cellInput(), width: 38, textAlign: 'center' }}
                        />
                      ))}
                    </div>
                  </td>

                  <td style={td(dense, { textAlign: 'right' })}>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={line.units || ''}
                      disabled={disabled}
                      onChange={(/** @type {any} */ e) => {
                        // Reject zero/negative units at the input boundary —
                        // backend validator also blocks but the UI shouldn't
                        // accept obvious garbage.
                        const raw = e.target.value;
                        if (raw === '') { updateLine(idx, { units: '' }); return; }
                        const n = Number(raw);
                        if (Number.isFinite(n) && n > 0) updateLine(idx, { units: raw });
                      }}
                      style={{ ...cellInput(), width: 56, textAlign: 'right' }}
                    />
                  </td>

                  <td style={td(dense, { textAlign: 'right' })}>
                    <ChargeCell
                      value={line.charge_amount || ''}
                      cpt={line.cpt_hcpcs_code || ''}
                      payerId={payerId}
                      onChange={(/** @type {string} */ amount) => updateLine(idx, { charge_amount: amount })}
                      density={mode === 'paper' ? 'paper' : 'default'}
                      disabled={disabled}
                    />
                  </td>

                  <td style={td(dense, {
                    textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap',
                  })}>
                    {line.charge_amount ? `$${Number(line.charge_amount).toFixed(2)}` : '—'}
                  </td>

                  <td style={td(dense)}>
                    <RenderingNpiCell
                      value={line.rendering_provider_npi || ''}
                      claimLevelNpi={claimRenderingNpi}
                      onChange={(/** @type {string} */ npi) => updateLine(idx, { rendering_provider_npi: npi })}
                      density={mode === 'paper' ? 'paper' : 'default'}
                      disabled={disabled}
                    />
                  </td>

                  <td style={td(dense, { textAlign: 'right' })}>
                    {!disabled && lines.length > 1 && (
                      <button
                        type="button"
                        aria-label={`Delete line ${idx + 1}`}
                        onClick={() => deleteLine(idx)}
                        style={{
                          background: 'transparent', border: 'none', cursor: 'pointer',
                          display: 'inline-flex', color: 'var(--ab-fg-subtle)',
                        }}
                      >
                        <Icon name="trash-2" size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {!disabled && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12 }}>
          <Btn kind="ghost" icon="plus" size="sm" onClick={addLine}>Add line</Btn>
          <div style={{ flex: 1 }} />
          <div style={{ fontSize: 12, color: 'var(--ab-fg-subtle)' }}>
            {lines.length} line{lines.length === 1 ? '' : 's'}
            {overrides > 0 ? ` · ${overrides} NPI override${overrides === 1 ? '' : 's'}` : ''}
          </div>
          <div style={{ fontSize: 14, fontWeight: 'var(--ab-weight-semi)' }}>
            Total: ${total.toFixed(2)}
          </div>
        </div>
      )}

      {!disabled && (/** @type {any[]} */ (commonCpt)).length > 0 && mode === 'guided' && (
        <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          <span style={{
            fontSize: 12, color: 'var(--ab-fg-subtle)',
            alignSelf: 'center', marginRight: 4,
          }}>Common:</span>
          {(/** @type {any[]} */ (commonCpt)).slice(0, 6).map((/** @type {any} */ c) => {
            const resolved = fee.resolveCharge(c.code);
            return (
              <span
                key={c.code}
                role="button"
                onClick={() => onChange([
                  ...lines,
                  {
                    ...emptyLine(claimDateOfService),
                    line_number: lines.length + 1,
                    cpt_hcpcs_code: c.code,
                    charge_amount: resolved.amount != null ? resolved.amount : '',
                  },
                ])}
                style={{
                  fontSize: 11,
                  padding: '3px 8px',
                  borderRadius: 999,
                  border: '1px solid var(--ab-border-hair)',
                  cursor: 'pointer',
                  color: 'var(--ab-fg-muted)',
                  background: 'var(--ab-bg)',
                  userSelect: 'none',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--ab-bg-alt)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--ab-bg)'; }}
              >
                {c.code}
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** @param {boolean} dense @param {'left'|'right'|'center'} [align] */
function th(dense, align = 'left') {
  return /** @type {Record<string, any>} */ ({
    padding: dense ? '4px 6px' : '10px 10px',
    textAlign: align,
    fontWeight: 700,
    fontSize: dense ? 9 : 11,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: 'var(--ab-fg-subtle)',
    borderBottom: '1px solid var(--ab-border-hair)',
    whiteSpace: 'nowrap',
  });
}

/** @param {boolean} dense @param {Record<string, any>} [extra] */
function td(dense, extra = {}) {
  return {
    padding: dense ? '4px 6px' : '6px 8px',
    verticalAlign: 'top',
    ...extra,
  };
}

function cellInput() {
  return /** @type {Record<string, any>} */ ({
    border: '1px solid transparent',
    background: 'transparent',
    fontFamily: 'var(--ab-font-sans)',
    fontSize: 'var(--ab-text-sm)',
    padding: '6px 8px',
    borderRadius: 4,
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    color: 'var(--ab-fg)',
  });
}

/** @param {boolean} dense @param {boolean} disabled */
function overrideBadge(dense, disabled) {
  return /** @type {Record<string, any>} */ ({
    display: 'inline-block', marginTop: 4,
    padding: '1px 5px',
    fontSize: dense ? 8 : 9,
    fontWeight: 'var(--ab-weight-bold)',
    letterSpacing: 'var(--ab-tracking-label)',
    background: 'var(--ab-pending-soft)',
    color: 'var(--ab-fg)',
    border: '0.5px solid var(--ab-pending-ring)',
    borderRadius: 'var(--ab-radius-sm)',
    cursor: disabled ? 'default' : 'pointer',
  });
}
